// ============================================
// 流月、流日、流时展示组件 - 默认全部展开
// ============================================

import { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from "@mui/material";
import { getCellColor, getGrey } from "../StyleControl";
import {
  getJieQiByMonth,
} from "../../utils/baziCalculator";

const scrollableSx = {
  display: "flex",
  flexWrap: "nowrap",
  overflowX: "auto",
  gap: 0.5,
  pb: 1,
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#c1c1c1",
    borderRadius: "3px",
  },
};

// 干支十神显示组件（天干地支左侧，十神右侧）
function GanZhiWithShen({ gan, zhi, ganShen, zhiShen }) {
  return (
    <Stack direction="column" spacing={0.1}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: "bold",
            color: getCellColor(null, gan),
            minWidth: 20,
          }}
        >
          {gan}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#666" }}>{ganShen}</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: "bold",
            color: getCellColor(null, zhi),
            minWidth: 20,
          }}
        >
          {zhi}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#666" }}>
          {zhiShen[0]}
        </Typography>
      </Stack>
    </Stack>
  );
}

// 获取时辰时间范围显示
function getShiChenTimeRange(shiChenIndex) {
  const ranges = [
    "23:00-1:00", // 子时
    "1:00-3:00", // 丑时
    "3:00-5:00", // 寅时
    "5:00-7:00", // 卯时
    "7:00-9:00", // 辰时
    "9:00-11:00", // 巳时
    "11:00-13:00", // 午时
    "13:00-15:00", // 未时
    "15:00-17:00", // 申时
    "17:00-19:00", // 酉时
    "19:00-21:00", // 戌时
    "21:00-23:00", // 亥时
  ];
  return ranges[shiChenIndex] || "";
}

// 流时展示组件
function LiuShiRow({ liuShiData, selectedShi, onSelect }) {
  if (!liuShiData) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color={getGrey()}
        sx={{ mb: 0.5, display: "block" }}
      >
        流时
      </Typography>
      <ToggleButtonGroup
        value={selectedShi}
        exclusive
        size="small"
        onChange={(e, value) => value !== null && onSelect(value)}
        sx={scrollableSx}
      >
        {Object.entries(liuShiData).map(([idx, data]) => (
          <ToggleButton
            key={idx}
            value={Number(idx)}
            sx={{
              p: 0.5,
              minWidth: 60,
              border: "1px solid #e0e0e0",
              flexShrink: 0,
            }}
          >
            <Stack alignItems="center" spacing={0.3}>
              <Typography sx={{ fontSize: 9, color: "#999" }}>
                {getShiChenTimeRange(Number(idx))}
              </Typography>
              <GanZhiWithShen
                gan={data.gan}
                zhi={data.zhi}
                ganShen={data.gan_shen}
                zhiShen={data.zhi_shen}
              />
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// 流日展示组件
function LiuRiPanel({
  liuRiData,
  selectedRi,
  onSelectRi,
  selectedShi,
  onSelectShi,
}) {
  if (!liuRiData) return null;

  const days = Object.entries(liuRiData);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color={getGrey()}
        sx={{ mb: 0.5, display: "block" }}
      >
        流日
      </Typography>
      <ToggleButtonGroup
        value={selectedRi}
        exclusive
        size="small"
        onChange={(e, value) => value !== null && onSelectRi(value)}
        sx={scrollableSx}
      >
        {days.map(([day, data]) => (
          <ToggleButton
            key={day}
            value={day}
            sx={{
              p: 0.5,
              minWidth: 60,
              border: "1px solid #e0e0e0",
              flexShrink: 0,
            }}
          >
            <Stack alignItems="center" spacing={0.3}>
              <Typography sx={{ fontSize: 9, color: "#999" }}>
                {day}
              </Typography>
              <GanZhiWithShen
                gan={data.gan}
                zhi={data.zhi}
                ganShen={data.gan_shen}
                zhiShen={data.zhi_shen}
              />
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {selectedRi && liuRiData[selectedRi]?.liuShi && (
        <LiuShiRow
          liuShiData={liuRiData[selectedRi].liuShi}
          selectedShi={selectedShi}
          onSelect={onSelectShi}
        />
      )}
    </Box>
  );
}

// 流月展示组件
function LiuYuePanel({
  liuYueData,
  selectedYue,
  onSelectYue,
  selectedRi,
  onSelectRi,
  selectedShi,
  onSelectShi,
  year,
  jieQiCache,
}) {
  // 使用传入的节气缓存
  const jieQiData = jieQiCache?.[year] || null;

  if (!liuYueData) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color={getGrey()}
        sx={{ mb: 0.5, display: "block" }}
      >
        流月
      </Typography>
      <ToggleButtonGroup
        value={selectedYue}
        exclusive
        size="small"
        onChange={(e, value) => value !== null && onSelectYue(value)}
        sx={scrollableSx}
      >
        {Object.entries(liuYueData).map(([month, data]) => {
          const jieQi = getJieQiByMonth(year, Number(month), jieQiData);
          return (
            <ToggleButton
              key={month}
              value={Number(month)}
              sx={{
                p: 0.5,
                minWidth: 60,
                border: "1px solid #e0e0e0",
                flexShrink: 0,
              }}
            >
              <Stack alignItems="center" spacing={0.3}>
                <Typography
                  sx={{ fontSize: 10, fontWeight: "bold", color: "#333" }}
                >
                  {jieQi.name}
                </Typography>
                <Typography sx={{ fontSize: 9, color: "#999" }}>
                  {jieQi.loading
                    ? "计算中..."
                    : jieQi.month > 0
                      ? `${jieQi.month}月${jieQi.day}日`
                      : ""}
                </Typography>
                <GanZhiWithShen
                  gan={data.gan}
                  zhi={data.zhi}
                  ganShen={data.gan_shen}
                  zhiShen={data.zhi_shen}
                />
              </Stack>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>

      {selectedYue && liuYueData[selectedYue]?.liuRi && (
        <LiuRiPanel
          liuRiData={liuYueData[selectedYue].liuRi}
          selectedRi={selectedRi}
          onSelectRi={onSelectRi}
          selectedShi={selectedShi}
          onSelectShi={onSelectShi}
        />
      )}
    </Box>
  );
}

export default function LiuYueRiShi({
  yuanGan,
  liuNianData,
  currentLiuNian,
  currentLiuYue,
  currentLiuRi,
  currentLiuShi,
  onChange,
  jieQiCache,
}) {
  const [selectedLiuNian, setSelectedLiuNian] = useState(
    currentLiuNian || null
  );
  const [selectedYue, setSelectedYue] = useState(currentLiuYue || null);
  const [selectedRi, setSelectedRi] = useState(currentLiuRi || null);
  const [selectedShi, setSelectedShi] = useState(currentLiuShi || null);

  useEffect(() => {
    if (currentLiuNian) {
      setSelectedLiuNian(currentLiuNian);
    }
  }, [currentLiuNian]);

  useEffect(() => {
    if (currentLiuYue) {
      setSelectedYue(currentLiuYue);
    }
  }, [currentLiuYue]);

  useEffect(() => {
    if (currentLiuRi) {
      setSelectedRi(currentLiuRi);
    }
  }, [currentLiuRi]);

  useEffect(() => {
    if (currentLiuShi !== undefined && currentLiuShi !== null) {
      setSelectedShi(currentLiuShi);
    }
  }, [currentLiuShi]);

  // 初始化时，如果没有选中的流月且父组件没有传入初始值，自动选择第一个
  useEffect(() => {
    if (selectedLiuNian && !selectedYue && !currentLiuYue && liuNianData[selectedLiuNian]?.liuYue) {
      const yueData = liuNianData[selectedLiuNian].liuYue;
      const firstYue = Object.keys(yueData)[0];
      if (firstYue) {
        handleYueSelect(Number(firstYue), true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLiuNian, liuNianData, currentLiuYue]);

  // 初始化时，如果已选择流日但没有选择流时，自动选择第一个流时
  useEffect(() => {
    if (selectedLiuNian && selectedYue && selectedRi && 
        (selectedShi === null || selectedShi === undefined) && 
        (currentLiuShi === null || currentLiuShi === undefined)) {
      const yueData = liuNianData[selectedLiuNian]?.liuYue?.[selectedYue];
      const riData = yueData?.liuRi?.[selectedRi];
      if (riData?.liuShi) {
        const firstShi = Object.keys(riData.liuShi)[0];
        if (firstShi) {
          handleShiSelect(Number(firstShi));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLiuNian, selectedYue, selectedRi, liuNianData, currentLiuShi]);

  if (!liuNianData || Object.keys(liuNianData).length === 0) {
    return <Typography color={getGrey()}>暂无流年数据</Typography>;
  }

  const handleLiuNianSelect = (year) => {
    setSelectedLiuNian(year);
    // 重置流月和流日选择
    setSelectedYue(null);
    setSelectedRi(null);
    onChange?.(year, null, null, selectedShi);
  };

  const handleYueSelect = (month, autoSelect = true) => {
    setSelectedYue(month);
    // 只有在需要自动选择流日时才选择该月的第一个流日
    // autoSelect 为 false 时表示使用父组件传入的初始值
    if (autoSelect) {
      const yueData = liuNianData[selectedLiuNian]?.liuYue?.[month];
      if (yueData?.liuRi) {
        const firstRi = Object.keys(yueData.liuRi)[0];
        if (firstRi) {
          setSelectedRi(firstRi);
          // 同时自动选择该日的第一个流时
          const riData = yueData.liuRi[firstRi];
          if (riData?.liuShi) {
            const firstShi = Object.keys(riData.liuShi)[0];
            if (firstShi) {
              const shiIndex = Number(firstShi);
              setSelectedShi(shiIndex);
              onChange?.(selectedLiuNian, month, firstRi, shiIndex);
              return;
            }
          }
          onChange?.(selectedLiuNian, month, firstRi, selectedShi);
          return;
        }
      }
    }
    onChange?.(selectedLiuNian, month, selectedRi, selectedShi);
  };

  const handleRiSelect = (day) => {
    setSelectedRi(day);
    // 如果没有已选择的流时，自动选择该日的第一个流时
    const yueData = liuNianData[selectedLiuNian]?.liuYue?.[selectedYue];
    const riData = yueData?.liuRi?.[day];
    if (riData?.liuShi) {
      const firstShi = Object.keys(riData.liuShi)[0];
      if (firstShi && (selectedShi === null || selectedShi === undefined)) {
        const shiIndex = Number(firstShi);
        setSelectedShi(shiIndex);
        onChange?.(selectedLiuNian, selectedYue, day, shiIndex);
        return;
      }
    }
    onChange?.(selectedLiuNian, selectedYue, day, selectedShi);
  };

  const handleShiSelect = (shi) => {
    setSelectedShi(shi);
    onChange?.(selectedLiuNian, selectedYue, selectedRi, shi);
  };

  return (
    <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
      <Typography variant="caption" color={getGrey()}>
        流年
      </Typography>

      <ToggleButtonGroup
        value={selectedLiuNian}
        exclusive
        size="small"
        onChange={(e, value) => value !== null && handleLiuNianSelect(value)}
        sx={scrollableSx}
      >
        {Object.entries(liuNianData).map(([year, data]) => (
          <ToggleButton
            key={year}
            value={Number(year)}
            sx={{
              p: 0.8,
              minWidth: 60,
              border: "1px solid #e0e0e0",
              flexShrink: 0,
            }}
          >
            <Stack alignItems="center" spacing={0.3}>
              <Typography sx={{ fontSize: 10, color: "#999" }}>
                {year}年
              </Typography>
              <GanZhiWithShen
                gan={data.gan}
                zhi={data.zhi}
                ganShen={data.gan_shen}
                zhiShen={data.zhi_shen}
              />
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {selectedLiuNian && liuNianData[selectedLiuNian]?.liuYue && (
        <LiuYuePanel
          liuYueData={liuNianData[selectedLiuNian].liuYue}
          selectedYue={selectedYue}
          onSelectYue={handleYueSelect}
          selectedRi={selectedRi}
          onSelectRi={handleRiSelect}
          selectedShi={selectedShi}
          onSelectShi={handleShiSelect}
          year={selectedLiuNian}
          jieQiCache={jieQiCache}
        />
      )}
    </Stack>
  );
}
